import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { FC } from 'react'

import { FormattedPrice } from '@src/blocks/currency'
import {
  getOfferPricesForProductCard,
  getPriceStyleKey as getOfferStyleKey,
} from '@src/quince-site-common/src/utils/festivalOffersUtility'

import { ProductTitleSectionProps } from './product-title-section'
import styles from './product-title-section.module.scss'

type PriceStyleKey = 'festivalPrice' | 'subscriptionPrice' | 'default'
const STYLE_KEY_MAP = {
  festival: 'festivalPrice' as const,
  subscription: 'subscriptionPrice' as const,
  default: 'default' as const,
}

const getPriceStyleKey = (festivalPrice?: number | null, subscriptionPrice?: number | null): PriceStyleKey => {
  return STYLE_KEY_MAP[getOfferStyleKey(!!festivalPrice, !!subscriptionPrice)]
}

const priceWrapperStylesToApply: Record<PriceStyleKey, (string | undefined)[]> = {
  festivalPrice: [styles.festivalOfferWrapper],
  subscriptionPrice: [styles.subscriptionOfferWrapper],
  default: [],
}

const priceTextStylesToApply: Record<PriceStyleKey, (string | undefined)[]> = {
  festivalPrice: [styles.festivalOfferText],
  subscriptionPrice: [styles.subscriptionOfferText],
  default: [],
}

const priceClassStylesToApply: Record<PriceStyleKey, (string | undefined)[]> = {
  festivalPrice: [styles.festivalPrice],
  subscriptionPrice: [styles.subscriptionPrice],
  default: [],
}

type ProductPriceProps = {
  isNotAttachedToProductTitleSection?: boolean
} & ProductTitleSectionProps

export const ProductPrice: FC<ProductPriceProps> = props => {
  const {
    selectedCardVariant,
    productPrefixPriceClassName,
    priceClassName,
    productPriceClass,
    isNotAttachedToProductTitleSection,
    subscriptionEnabled,
    productPromoConfig,
  } = props
  const { t } = useTranslation('common')
  const salePrice = selectedCardVariant?.price?.salePrice
  const showFrom = selectedCardVariant?.price?.isRange

  const { festivalPrice, subscriptionPrice } = getOfferPricesForProductCard(
    Boolean(subscriptionEnabled),
    selectedCardVariant,
    productPromoConfig,
  )

  return (
    <div className={styles.nameAndPriceSection}>
      {isNotAttachedToProductTitleSection ? (
        <div className={classnames(styles.notAttachedToProductTitleSectionProductPriceWrapper, productPriceClass)}>
          <div className={classnames(styles.festivalOfferText)}>
            {showFrom && (
              <div className={classnames(styles.productPrefixPrice, productPrefixPriceClassName)}>{t('from')}</div>
            )}
            <div
              className={classnames(priceClassName, {
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 means "no price", must fall through
                [`${styles.basePrice}`]: festivalPrice || subscriptionPrice,
              })}
            >
              <FormattedPrice amount={salePrice} />
            </div>
          </div>
          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 means "no price", must fall through */}
          {(festivalPrice || subscriptionPrice) && (
            <div
              className={classnames(
                props.priceClassName,
                ...priceClassStylesToApply[getPriceStyleKey(festivalPrice, subscriptionPrice)],
              )}
            >
              {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 means "no price", must fall through */}
              <FormattedPrice amount={subscriptionPrice || festivalPrice || 0} />
            </div>
          )}
        </div>
      ) : (
        <div
          className={classnames(
            styles.productPriceWrapper,
            productPriceClass,
            ...priceWrapperStylesToApply[getPriceStyleKey(festivalPrice, subscriptionPrice)],
          )}
        >
          <div className={classnames(...priceTextStylesToApply[getPriceStyleKey(festivalPrice, subscriptionPrice)])}>
            {showFrom && (
              <div className={classnames(styles.productPrefixPrice, productPrefixPriceClassName)}>{t('from')}</div>
            )}
            {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 means "no price", must fall through */}
            {(festivalPrice || subscriptionPrice) && (
              <div
                className={classnames(
                  props.priceClassName,
                  ...priceClassStylesToApply[getPriceStyleKey(festivalPrice, subscriptionPrice)],
                )}
              >
                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 means "no price", must fall through */}
                <FormattedPrice amount={subscriptionPrice || festivalPrice || salePrice} />
              </div>
            )}
          </div>
          <div className={classnames(priceClassName, styles.basePrice)}>
            <FormattedPrice amount={salePrice} />
          </div>
        </div>
      )}
    </div>
  )
}
